/**
 * Homepage Tile
 */

import React from 'react';
import PropTypes from 'prop-types';

import { isEqual } from 'lodash';
import classNames from 'classnames';
import autobind from 'class-autobind';
import { dataLayer, isEecPromo } from 'utils/analytics';

import HomepageTileAsset from 'components/Homepage/HomepageTileAsset';
import Link from 'components/Link';
import Markdown from 'components/Markdown';

import styled from 'styled-components';
import styles from './styles.css';

const HomepageTileContainer = styled.article`
  && {
    background-color: ${props => props.backgroundColor};

    .${styles.headline} {
      color: ${props => props.headlineColor};
    }

    .${styles.text} {
      color: ${props => props.textColor};
    }

    .${styles.assetContainer} {
      ${props => props.imageEffects}
    }

    ${props => props.svgLayerColors.map(layer => `#${layer.layerName}{ -webkit-transition: fill 0.2s ease-in-out 0s; transition: fill 0.2s ease-in-out 0s; fill: ${layer.color}}; }`)}

    &&:focus,
    &&:hover {
      background-color: ${props => props.backgroundColorHover || props.backgroundColor};

      .${styles.headline} {
        color: ${props => props.headlineColorHover || props.headlineColor};
      }

      .${styles.text} {
        color: ${props => props.textColorHover || props.textColor};
      }

      .${styles.assetContainer} {
        ${props => props.imageEffectsHover}
      }

      ${props => props.svgLayerColors.map(layer => `#${layer.layerName}{ -webkit-transition: background-color 0.2s ease-in-out 0s; transition: fill 0.2s ease-in-out 0s; fill: ${layer.colorHover}; }`)}
    }
  }
`;

export class HomepageTile extends React.Component {
	constructor(props) {
		super(props);
		autobind(this);

		this.state = {
			asset: null,
			assetLoaded: false,
			assetHover: null,
			assetHoverLoaded: false,
			isHovering: false,
			videoCurrentTime: 0
		};
	}

	componentDidMount() {
		this.setAssets();
	}

	componentDidUpdate(prevProps, prevState) {
		const { onLoad, layout } = this.props;
		if ((this.assetsLoaded(this.state) && !this.assetsLoaded(prevState)) || layout !== prevProps.layout) {
			onLoad();
		}

		if (layout !== prevProps.layout) {
			this.setAssets();
		}
	}

	onMouseEnter() {
		this.setState({ isHovering: true });
	}

	onMouseLeave() {
		this.setState({ isHovering: false });
	}

	onVideoPause(videoElement) {
		this.setState({ videoCurrentTime: videoElement.currentTime });
	}

	onVideoPlay(videoElement) {
		const { videoCurrentTime } = this.state;
		videoElement.currentTime = videoCurrentTime;
	}

	onAssetLoad() {
		this.setState({ assetLoaded: true });
	}

	onAssetHoverLoad() {
		this.setState({ assetHoverLoaded: true });
	}

	setAssets() {
		const {
			assets, layout, videoOnMobile
		} = this.props;
		const { single, double, triple } = assets;
		const isTouchDevice = 'ontouchstart' in document.documentElement;
		const disableVideo = isTouchDevice && !videoOnMobile;

		let asset = null;
		let assetHover = null;

		function setAssets(sizeAssets) {
			const { embed, image, video } = sizeAssets;
			const sizeAsset = disableVideo ? image.asset : (video.asset || embed.asset || image.asset);
			const sizeAssetHover = disableVideo ? image.assetHover : (video.assetHover || embed.assetHover || image.assetHover);
			assetHover = sizeAssetHover || sizeAsset || assetHover || asset;
			asset = sizeAsset || asset;
		}

		setAssets(single);

		if (layout === 'tablet' || layout === 'desktop') {
			setAssets(double);
		}

		if (layout === 'desktop') {
			setAssets(triple);
		}

		this.setState({ asset, assetHover });
	}

	dataLayerPromoClick() {
		// const { id, moduleLink, title: name, position } = this.props;
		// if (isEecPromo(moduleLink)) {
		// 	dataLayer({
		// 		event: 'eec_promo_click',
		// 		eventCat: 'ecommerce',
		// 		eventAct: 'promotion click',
		// 		eventLab: name,
		// 		eventVal: 0,
		// 		nonInteraction: false,
		// 		ecommerce: {
		// 			promoClick: {
		// 				promotions: [
		// 					{
		// 						creative: 'homepageTile',
		// 						name,
		// 						id: `homepageTile_${id}`,
		// 						position: `${position}`
		// 					}
		// 				]
		// 			}
		// 		}
		// 	});
		// }
	}

	noAssetHover() {
		const { asset, assetHover } = this.state;
		return !assetHover || isEqual(asset, assetHover);
	}

	assetsLoaded(state) {
		const { assetLoaded, assetHoverLoaded } = state;
		return this.noAssetHover() ? assetLoaded : assetLoaded && assetHoverLoaded;
	}

	render() {
		const { asset, assetHover, isHovering } = this.state;
		const {
			backgroundColor, backgroundColorHover, className,
			fontsLoaded, headline, headlineColor, headlineColorHover, homepageUrlTitle,
			imageEffects, imageEffectsHover, isAnimatedSVG, isVisible, moduleLink,
			scrollPercentage, size, svgLayerColors, text, textColor, textColorHover,
			title, urlTitle
		} = this.props;

		const noAssetHover = this.noAssetHover();

		const assetsLoaded = isVisible && this.assetsLoaded(this.state);

		return (
			<HomepageTileContainer
				backgroundColor={ backgroundColor }
				backgroundColorHover={ backgroundColorHover }
				className={ classNames(className, styles.homepageTile, styles[size]) }
				headlineColor={ headlineColor }
				headlineColorHover={ headlineColorHover }
				imageEffects={ imageEffects }
				imageEffectsHover={ imageEffectsHover }
				onMouseEnter={ this.onMouseEnter }
				onMouseLeave={ this.onMouseLeave }
				svgLayerColors={ svgLayerColors }
				textColor={ textColor }
				textColorHover={ textColorHover }
			>
				<Link
					className={ styles.container }
					onClick={ this.dataLayerPromoClick }
					to={ moduleLink }
				>
					<figure className={ classNames(styles.assetContainer, assetsLoaded && styles.assetsLoaded) }>
						{ asset && <HomepageTileAsset
							asset={ asset }
							className={ classNames(styles.asset, noAssetHover && styles.noAssetHover) }
							isAnimatedSVG={ isAnimatedSVG }
							isHovering={ isHovering }
							isRecolorableSVG={ svgLayerColors.length > 0 }
							onLoad={ this.onAssetLoad }
							scrollPercentage={ scrollPercentage }
							size={ size }
							title={ title }
							videoOptions={ {
								autoplay: false,
								isPlaying: !isHovering,
								onPause: this.onVideoPause,
								onPlay: this.onVideoPlay
							} }
						/> }
						{ !noAssetHover && <HomepageTileAsset
							asset={ assetHover }
							className={ styles.assetHover }
							isAnimatedSVG={ isAnimatedSVG }
							isHovering={ isHovering }
							isRecolorableSVG={ svgLayerColors.length > 0 }
							onLoad={ this.onAssetHoverLoad }
							scrollPercentage={ scrollPercentage }
							size={ size }
							title={ title }
							videoOptions={ {
								autoplay: false,
								isPlaying: isHovering,
								onPause: this.onVideoPause,
								onPlay: this.onVideoPlay
							} }
						/> }
					</figure>
					<section className={ classNames(styles.textContainer, fontsLoaded && styles.fontsLoaded) }>
						<Markdown className={ styles.headline } content={ headline } tagName="h2"></Markdown>
						{ text && <Markdown className={ styles.text } content={ text } tagName="div"></Markdown> }
					</section>
				</Link>
			</HomepageTileContainer>
		);
	}
}

HomepageTile.propTypes = {
	assets: PropTypes.object.isRequired,
	backgroundColor: PropTypes.string,
	backgroundColorHover: PropTypes.string,
	className: PropTypes.string,
	fontsLoaded: PropTypes.bool,
	headline: PropTypes.string.isRequired,
	headlineColor: PropTypes.string,
	headlineColorHover: PropTypes.string,
	homepageUrlTitle: PropTypes.string.isRequired,
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	imageEffects: PropTypes.string,
	imageEffectsHover: PropTypes.string,
	isAnimatedSVG: PropTypes.bool,
	isVisible: PropTypes.bool,
	layout: PropTypes.string.isRequired,
	moduleLink: PropTypes.string.isRequired,
	onLoad: PropTypes.func.isRequired,
	position: PropTypes.number.isRequired,
	scrollPercentage: PropTypes.number,
	size: PropTypes.string.isRequired,
	svgLayerColors: PropTypes.array,
	text: PropTypes.string,
	textColor: PropTypes.string,
	textColorHover: PropTypes.string,
	title: PropTypes.string.isRequired,
	videoOnMobile: PropTypes.bool,
	urlTitle: PropTypes.string.isRequired
};

HomepageTile.defaultProps = {
	backgroundColor: null,
	backgroundColorHover: null,
	className: null,
	fontsLoaded: false,
	headlineColor: null,
	headlineColorHover: null,
	imageEffects: null,
	imageEffectsHover: null,
	isAnimatedSVG: false,
	isVisible: false,
	scrollPercentage: null,
	svgLayerColors: [],
	text: null,
	textColor: null,
	textColorHover: null,
	videoOnMobile: false
};

export default HomepageTile;
